import { useRef, useState } from "react";

interface AnimatedMasonryGridProps {
  items: string[];
  onItemClick: (item: string) => void;
}

export default function Card({
  title,
  description,
  onClick,
}: {
  title: string;
  description: string;
  onClick?: () => void;
}) {
  return (
    <div
      className="tw:max-w-72 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-sm tw:p-4 hover:tw:shadow-lg hover:tw:border-gray-300 tw:transition-all tw:duration-200 tw:cursor-pointer tw:text-left"
      onClick={onClick}
    >
      <div className="tw:space-y-2">
        <h4 className="tw:text-sm tw:font-semibold tw:text-gray-900 tw:leading-tight">
          {title}
        </h4>
        <p className="tw:text-sm tw:text-gray-600 tw:leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

export const AnimatedMasonryGrid = ({
  items,
  onItemClick,
}: AnimatedMasonryGridProps) => {
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Split items into 3 rows for masonry effect
  const rows = [
    items.filter((_, i) => i % 3 === 0),
    items.filter((_, i) => i % 3 === 1),
    items.filter((_, i) => i % 3 === 2),
  ];

  // Duplicate items for seamless loop
  const duplicatedRows = rows.map((row) => [...row, ...row]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        overflow: "hidden",
        position: "relative",
        maskImage:
          "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <style>
        {`
          @keyframes scroll-left {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }

          @keyframes scroll-right {
            0% {
              transform: translateX(-50%);
            }
            100% {
              transform: translateX(0);
            }
          }

          .animate-scroll-left {
            animation: scroll-left 40s linear infinite;
          }

          .animate-scroll-right {
            animation: scroll-right 45s linear infinite;
          }

          .paused {
            animation-play-state: paused !important;
          }
        `}
      </style>

      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {duplicatedRows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={`${
              rowIndex % 2 === 0
                ? "animate-scroll-left"
                : "animate-scroll-right"
            } ${isPaused ? "paused" : ""}`}
            style={{
              display: "flex",
              gap: "8px",
              width: "max-content",
            }}
          >
            {row.map((item, itemIndex) => (
              <Card
                key={`${rowIndex}-${itemIndex}`}
                title={"Suggested search"}
                description={item}
                onClick={() => onItemClick(item)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
